/**
 * @class msysMessage_file
 * @description
 * Provides functions to work with .mm files
 */

const binary = require('./binary.js')
const fs = typeof process === 'object' ? require('fs') || {} : {}
const fsp = fs?.promises || {}
const path = typeof process === 'object' ? require('path') || {} : {}
const ms = {
  ...require('./base.js'),
  ...require('./binary.js'),
}

const busy_files = {}

module.exports = {
  lockFile,
  unlockFile,
  getFilePath,
  getPartialFilePath,
  parseFileDatetime,
  appendFile,
  readBufferBlocks,
  readBufferMessages,
  readFileMessages,
  writeFileMessages,
  filterFiles,
  readFileBlocks,
  writeFileBlocks,
  searchBlocks,
  filterBlocks,
  testFile,
  testFileHasActionId,
  testFileHasMessage,
  findLastMessage,
}

function asyncTimeout(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
} // asyncTimeout

async function lockFile(path) {
  while(busy_files[path]) {
    await asyncTimeout(1)
  }
  busy_files[path] = 1
} // lockFile

function unlockFile(path) {
  delete busy_files[path]
} // unlockFile

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Constructs relative file path for .mm file based on given timestamp in following structure:
 * Input is time in microseconds
 * yyyymmdd/hhmm/yyyymmddhhmmss.mm
 * Important: it is GMT time !!!
 */
function getFilePath(root, event_dt, encrypt) {
  if(!(event_dt >= 1_000_000_000_000_000)) return []
  const dt = new Date(Math.floor(event_dt/1_000))
  const ymd = dt.getUTCFullYear() + String(dt.getUTCMonth()+1).padStart(2, '0') + String(dt.getUTCDate()).padStart(2, '0')
  const hhmm = String(dt.getUTCHours()).padStart(2, '0') + String(dt.getUTCMinutes()).padStart(2, '0')
  const dir = path.resolve(root||process.env.HOME, ymd + '/' + hhmm)
  const file = dir + '/' + ymd + hhmm + String(dt.getUTCSeconds()).padStart(2, '0') + (encrypt ? '.mx' : '.mm')
  return [file, dir]
} // getFilePath

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Parses date/time info from .mm (.mx) file path
 * Important: it is GMT time !!!
 */
function parseFileDatetime(file) {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})\.m[mx]$/.exec(path.basename(file))
  if(!m) return {}
  const [, year, month, day, hour, minute, second] = m
  const dt = new Date(`${year}-${month}-${day} ${hour}:${minute}:${second} Z`)
  const sec = Math.floor(dt.valueOf()/1000)
  return { year, month, day, hour, minute, second, sec }
} // parseFileDatetime

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Constructs relative partial file path for .mm file based on given timestamp in following structure:
 * yyyymmdd/hhmm/yyyymmddhhmm
 * Input is time in microseconds
 * Important: it is GMT time !!!
 * Useful for reuse of this partial path for multiple seconds
 */
function getPartialFilePath(root, event_dt) {
  if(!(event_dt >= 1_000_000_000_000_000)) return []
  const dt = new Date(Math.floor(event_dt/1_000))
  const ymd = dt.getUTCFullYear() + String(dt.getUTCMonth()+1).padStart(2, '0') + String(dt.getUTCDate()).padStart(2, '0')
  const hhmm = String(dt.getUTCHours()).padStart(2, '0') + String(dt.getUTCMinutes()).padStart(2, '0')
  const dir = path.resolve(root||process.env.HOME, ymd + '/' + hhmm)
  return dir + '/' + ymd + hhmm
} // getPartialFilePath

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Append
 */
async function appendFile(root, msg) {
  const params = msg && typeof (msg)==='object' ? msg.params : null
  const event_dt = params && typeof (params)==='object' ? params.event_dt : null
  const [path, dir] = getFilePath(root, event_dt)
  if(!path) return
  await fsp.mkdir(dir, { recursive: true })
  const block = binary.msg2uint(msg, true)
  await lockFile(path)
  await fsp.appendFile(path, block)
  unlockFile(path)
  return path
} // appendFile

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Reads messages from Buffer
 */
function readBufferMessages(buf, filter_function) {
  const messages = []
  let msg = null
  let size = 0
  try{
    while(buf.length>4) {
      size = (buf[0]<<16) + (buf[1]<<8) + buf[2]
      if(buf.length<size+3) break // incomplete last block
      msg = binary.uint2msg(buf.slice(3, size+3))
      buf = buf.slice(size+3)
      if(msg) {
        if(!filter_function || filter_function(msg.sender, msg.target, msg.action, msg.params)) { messages.push(msg) }
        msg = null
      }
    }
  }catch(e) {}
  buf = null
  return messages
} // readBufferMessages

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Reads messages from file
 */
async function readFileMessages(file_path, filter_function) {
  let messages = []
  let buf = null
  try{
    buf = await fsp.readFile(file_path)
    messages = readBufferMessages(buf, filter_function)
  }catch(e) {}
  buf = null
  return messages
} // readFileMessages

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Writes or appends message or array of messages to file
 */
async function writeFileMessages(file_path, messages, append) {
  if(!Array.isArray(messages)) messages = [messages]
  await lockFile(file_path)
  let result
  try{
    await fsp.mkdir(path.dirname(file_path), { recursive: true })
    if(!append) await fsp.writeFile(file_path, '') // clear file content
    for(const msg of messages) {
      if(!msg || typeof (msg)!=='object') continue
      const block = binary.msg2uint(msg, true)
      await fsp.appendFile(file_path, block)
    }
    result = true
  }catch(e) {
    console.error(e)
    result = false
  }
  await unlockFile(file_path)
  return result
} // writeFileMessages

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Reads unparsed blocks from buffer
 */
async function readBufferBlocks(buf, async_test_fcn) {
  const blocks = []
  let size = 0
  try{
    while(buf.length>4) {
      size = (buf[0]<<16) + (buf[1]<<8) + buf[2]
      if(buf.length<size+3) break // incomplete last block
      const block = buf.slice(3, size+3)
      if(!async_test_fcn || await async_test_fcn(block)) {
        blocks.push(block)
      }
      buf = buf.slice(size+3)
    }
  }catch(e) {}
  buf = null
  return blocks
} // readBufferBlocks

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Reads unparsed blocks from file
 */
async function readFileBlocks(file_path) {
  const blocks = []
  let size = 0
  let buf = null
  try{
    buf = await fsp.readFile(file_path)
    while(buf.length>4) {
      size = (buf[0]<<16) + (buf[1]<<8) + buf[2]
      if(buf.length<size+3) break // incomplete last block
      blocks.push(buf.slice(3, size+3))
      buf = buf.slice(size+3)
    }
  }catch(e) {}
  buf = null
  return blocks
} // readFileBlocks

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Writes unparsed blocks to file
 */
async function writeFileBlocks(file_path, blocks, append) {
  if(!blocks) return false
  if(!Array.isArray(blocks)) blocks = [blocks]
  await lockFile(file_path)
  let result
  try{
    await fsp.mkdir(path.dirname(file_path), { recursive: true })
    if(!append) await fsp.writeFile(file_path, '') // clear file content
    for(const block of blocks) {
      if(!block || !(block instanceof Buffer)) continue
      const size = block.length
      const header = Buffer.from([(size>>16)&0xFF, (size>>8)&0xFF, size&0xFF])
      await fsp.appendFile(file_path, header)
      await fsp.appendFile(file_path, block)
    }
    result = true
  }catch(e) {
    console.error(e)
    result = false
  }
  await unlockFile(file_path)
  return result
} // writeFileBlocks

async function searchBlocks(root, start_us, end_us, forward, async_test_fcn) {
  const start_sec = Math.floor(start_us/1_000_000)
  const end_sec = Math.floor(end_us/1_000_000)
  let blocks = null
  let partial_path
  if(forward) {
    partial_path = getPartialFilePath(root, start_us)
    for(let sec=start_sec; sec<=end_sec; sec++) {
      const second = sec % 60
      if(second === 0) partial_path = getPartialFilePath(root, sec*1_000_000)
      blocks = await readFileBlocks(partial_path+String(second).padStart(2, '0')+'.mm')
      if(sec === start_sec) {
        if(sec === end_sec) {
          blocks = blocks.filter(b=>{
            const [dt] = binary.uint2dtactionid(b)
            return dt>=start_us && dt<=end_us
          })
        }else {
          blocks = blocks.filter(b=>{
            const [dt] = binary.uint2dtactionid(b)
            return dt>=start_us
          })
        }
      }else if(sec === end_sec) {
        blocks = blocks.filter(b=>{
          const [dt] = binary.uint2dtactionid(b)
          return dt<=end_us
        })
      }
      const test = await async_test_fcn(sec, blocks)
      blocks = null
      if(test!=undefined) return test
    }
  }else{
    partial_path = getPartialFilePath(root, end_us)
    for(let sec=end_sec; sec>=start_sec; sec--) {
      const second = sec % 60
      if(second === 59) partial_path = getPartialFilePath(root, sec*1_000_000)
      blocks = await readFileBlocks(partial_path+String(second).padStart(2, '0')+'.mm')
      if(sec === start_sec) {
        if(sec === end_sec) {
          blocks = blocks.filter(b=>{
            const [dt] = binary.uint2dtactionid(b)
            return dt>=start_us && dt<=end_us
          })
        }else {
          blocks = blocks.filter(b=>{
            const [dt] = binary.uint2dtactionid(b)
            return dt>=start_us
          })
        }
      }else if(sec === end_sec) {
        blocks = blocks.filter(b=>{
          const [dt] = binary.uint2dtactionid(b)
          return dt<=end_us
        })
      }
      const test = await async_test_fcn(sec, blocks)
      blocks = null
      if(test!=undefined) return test
    }
  }
} // searchBlocks

async function filterBlocks(root, start_us, end_us, async_filter_fcn, pauses) {
  const start_sec = Math.floor(start_us/1_000_000)
  const end_sec = Math.floor(end_us/1_000_000)
  pauses = parseInt(pauses)
  pauses = pauses>0 ? pauses : 0
  let out = []
  let partial_path = getPartialFilePath(root, start_us)
  for(let sec=start_sec; sec<=end_sec; sec++) {
    const second = sec % 60
    if(second === 0) partial_path = getPartialFilePath(root, sec*1_000_000)
    let blocks = await readFileBlocks(partial_path+String(second).padStart(2, '0')+'.mm')
    if(pauses>0) await asyncTimeout(pauses)
    if(sec === start_sec) {
      if(sec === end_sec) {
        blocks = blocks.filter(b=>{
          const [dt] = binary.uint2dtactionid(b)
          return dt>=start_us && dt<=end_us
        })
      }else {
        blocks = blocks.filter(b=>{
          const [dt] = binary.uint2dtactionid(b)
          return dt>=start_us
        })
      }
    }else if(sec === end_sec) {
      blocks = blocks.filter(b=>{
        const [dt] = binary.uint2dtactionid(b)
        return dt<=end_us
      })
    }
    const filtered = await async_filter_fcn(sec, blocks)
    if(filtered.length>0) out = [...out, ...filtered]
    if(pauses>0) await asyncTimeout(pauses)
  }
  return out
} // filterBlocks

async function filterFiles(root, start_us, end_us, filter_function, pauses) {
  start_us = parseInt(start_us)
  end_us = parseInt(end_us)
  pauses = parseInt(pauses)
  pauses = pauses>0 ? pauses : 0

  if(!(start_us>=1_500_000_000_000_000 && end_us>=start_us)) return { error: 'invalid times' }
  if(typeof filter_function!=='function') return { error: 'invalid filter' }

  const start_sec = Math.floor(start_us/1_000_000)
  const end_sec = Math.floor(end_us/1_000_000)
  let partial_path = getPartialFilePath(root, start_sec*1_000_000)
  let before = 0
  let after = 0
  let files = 0
  const changed = []
  for(let sec=start_sec; sec<=end_sec; sec++) {
    const second = sec % 60
    if(second === 0) partial_path = getPartialFilePath(root, sec*1_000_000)
    const file = partial_path+String(second).padStart(2, '0')+'.mm'
    let messages = await readFileMessages(file)
    const count = messages.length
    before += count
    messages = messages.filter(msg=>msg.params.event_dt<start_us || msg.params.event_dt>end_us || filter_function(msg.sender, msg.target, msg.action, msg.params))
    if(messages.length<count) {
      changed.push(sec)
      await writeFileMessages(file, messages)
    }
    after += messages.length
    if(count>0) files++
    if(pauses>0) await asyncTimeout(pauses)
  }
  const deleted = before - after
  return { before, after, deleted, files, changed }
} // filterFiles

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Verifies if the file exists
 */
async function testFile(path) {
  try{
    return (await fsp.stat(path)).isFile()
  }catch(e) {
    return false
  }
} // testFile

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Verifies if the file exists and has inside at least one message with given action
 */
async function testFileHasActionId(required_action_id, path) {
  const actions = Array.isArray(required_action_id) ? required_action_id : required_action_id ? [required_action_id] : []
  const buf_length = 256
  const buf = Buffer.allocUnsafe(buf_length)
  let pos = 0
  let fh = null
  try{
    fh = await fsp.open(path)
  }catch(e) {
    return false
  }
  let match = false
  try{
    while(1) {
      const { bytesRead } = await fh.read(buf, 0, buf_length, pos)
      if(bytesRead<5) break
      const msg_size = (buf[0]<<16) + (buf[1]<<8) + buf[2]
      let action_id
      if((buf[15]>>5)===0b101) {
        action_id = buf.slice(16, 16+buf[15]-0xA0).toString()
      }else{
        action_id = binary.uint2dtactionid(buf, true)[1]
      }
      if(actions.indexOf(action_id)>=0) {
        match = true
        break
      }
      pos += msg_size+3
    }
  }catch(e) {
    console.error('Error testing file action: '+e)
  }
  try{ await fh.close() }catch(e) {}
  return match
} // testFileHasActionId

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Verifies if the file exists and has inside at least one message matching given message filter
 */
async function testFileHasMessage(filter_function, path) {
  try{
    let buf = await fsp.readFile(path)
    while(buf.length>4) {
      const size = (buf[0]<<16) + (buf[1]<<8) + buf[2]
      if(buf.length<size+3) return false
      const block = buf.slice(3, size+3)
      buf = buf.slice(size+3)
      const msg = binary.uint2msg(block)
      if(msg && filter_function(ms.address2string(msg.sender), ms.address2string(msg.target), msg.action, msg.params)) {
        return true
      }
    }
  }catch(e) {
    // console.error(e)
  }
  return false
} // testFileHasMessage

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Finds latest message in a file matching given index+action
 */
async function findLastMessage(root, index, action, max_days = 366) {
  if(!root || !index || !action) return
  if(!fs.existsSync) return // fs not available in browser
  const action_id = ms.action2id(action)
  if(!action_id) return
  const end_day = Math.floor(Date.now() / 86_400_000)
  const start_day = end_day - max_days

  let tested_days = 0
  for(let day=end_day; day >= start_day; day--) {
    const dt = new Date(day * 86_400_000)
    const yyyy = dt.getUTCFullYear()
    const m = dt.getUTCMonth()+1
    const mm = String(m<10 ? '0'+m : m)
    const d = dt.getUTCDate()
    const dd = d<10 ? '0'+d : d
    const ymd = yyyy + mm + dd
    const mi_path = `${root}/${ymd}/${index}_${ymd}.mi`
    if(!fs.existsSync(mi_path)) continue

    // read index file to memory
    let bytes
    try {
      bytes = await fsp.readFile(mi_path)
    }catch(e) {
      continue
    }

    // go through bytes backwards to find last message
    for(let byte_idx=bytes.length-1; byte_idx>=0; byte_idx--) {
      const byte = bytes[byte_idx]
      if(byte===0) continue // no bit indicating message presence set
      let bit_idx = 7
      while(1) {
        while((byte & (1 << bit_idx)) === 0 && bit_idx >= 0) bit_idx--
        if(bit_idx < 0) break // no more bits set
        const second = byte_idx * 8 + bit_idx

        // we have candidate, a bit set in index file representing a second
        // where a message should be present
        // however we have to verify if the message really is in .mm file
        const s = second % 60
        const ss = s < 10 ? '0' + s : s
        const m = Math.floor(second / 60) % 60
        const mm = m < 10 ? '0' + m : m
        const h = Math.floor(second / 3600) % 24
        const hh = h < 10 ? '0' + h : h
        const hhmm = `${hh}${mm}`
        const mm_path = `${root}/${ymd}/${hhmm}/${ymd}${hhmm}${ss}.mm`
        const blocks = await readFileBlocks(mm_path)
        for(let i=blocks.length-1; i>=0; i--) {
          // check block in reverse order for action_id matching
          const [, aid] = ms.uint2dtactionid(blocks[i])
          if(aid===action_id) {
            const msg = ms.uint2msg(blocks[i])
            return msg
          }
        }

        // candidate failed; despite it is marked in index file
        // the message with given action actually is not present in .mm file
        // we have to try another bit within the same byte
        if(bit_idx === 0) break // all bits checked in the byte
        bit_idx--
      }
    }

    tested_days++
    if(tested_days % 10 === 0) {
      await new Promise(resolve => setTimeout(resolve, 1))
    }
  }
} // findLastMessage
